import type { History } from 'history';
import appState from '../state/AppStateContainer';
import { loadStored, saveStored } from './userScopedStorage';

export type EvaluationsView = 'project' | 'supplier';

export type WizardSeedSupplier = {
  _id: string;
  name: string;
  /** The purchase it won; without one the wizard's own rule picks the purchase. */
  areaId?: string;
};

export type WizardSeed = {
  project: { _id: string; name: string; number: string | null } | null;
  suppliers: WizardSeedSupplier[];
  /** The project's winners are chosen once the project has loaded. */
  preselectWinners?: boolean;
};

/** The one location state every push into the overview, the wizard or a detail page carries. */
export type EvaluationsLocationState = { from?: string; seed?: WizardSeed };

export const EVALUATIONS_VIEW_KEY = 'evaluationsOverviewView';

export const lastEvaluationsSubPath = (): '/evaluations' | '/evaluations/by-supplier' =>
  loadStored(EVALUATIONS_VIEW_KEY).view === 'supplier'
    ? '/evaluations/by-supplier'
    : '/evaluations';

export const rememberEvaluationsView = (view: EvaluationsView) =>
  saveStored(EVALUATIONS_VIEW_KEY, { view });

export const evaluationsUrl = (subPath = '') =>
  `${appState._getCurrentOrgUrl()}/supplier-database/evaluations${subPath}`;

export const lastEvaluationsUrl = () =>
  `${appState._getCurrentOrgUrl()}/supplier-database${lastEvaluationsSubPath()}`;

export const registerSupplierUrl = (supplierId: string) =>
  `${appState._getCurrentOrgUrl()}/supplier-database?supplier=${supplierId}&tab=evaluations`;

export const registerProfileUrl = (supplierId: string) =>
  `${appState._getCurrentOrgUrl()}/supplier-database?supplier=${supplierId}`;

/** The supplier's evaluation profile: Per leverantör focused on it, with its row open. */
export const supplierEvaluationsUrl = (supplierId: string) =>
  evaluationsUrl(`/by-supplier?open=${supplierId}&supplier=${supplierId}`);

export const wizardUrl = () => evaluationsUrl('/new');

// The wizard's Avbryt and Back step back one entry, so the entry under it must be `from`.
export const openWizard = (history: History, seed: WizardSeed, from: string) => {
  const { pathname, search } = history.location;
  if (`${pathname}${search}` !== from) history.replace(from);
  history.push(wizardUrl(), { from, seed });
};

type ProjectToEvaluate = { _id: string; name: string; number?: string | null };

/** The wizard on an existing project, with the project's winners chosen once it has loaded. */
export const openProjectWizard = (history: History, project: ProjectToEvaluate, from: string) =>
  openWizard(
    history,
    {
      project: { _id: project._id, name: project.name, number: project.number || null },
      suppliers: [],
      preselectWinners: true,
    },
    from,
  );

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === 'string';

const isStringOrNull = (value: unknown) => value === null || isString(value);

const isListOf = (value: unknown, isItem: (item: unknown) => boolean) =>
  Array.isArray(value) && value.every(isItem);

// Only a path inside the current org, like the answer page's ?back=: never a url that leaves the app.
export const fromOf = (state: unknown): string | null => {
  const from = isRecord(state) ? state.from : undefined;
  const orgUrl = appState._getCurrentOrgUrl();
  if (!isString(from) || !orgUrl || !from.startsWith(`${orgUrl}/`)) return null;
  const [path] = from.split(/[?#]/);
  return path.includes('//') || path.includes(':') ? null : from;
};

const statusFromKey = (requestId: string) => `evaluationStatusFrom:${requestId}`;

// Svara nu leaves the app with a full page load, which drops the status page's location state.
export const rememberStatusFrom = (requestId: string, from: string | null) => {
  try {
    if (from) sessionStorage.setItem(statusFromKey(requestId), from);
    else sessionStorage.removeItem(statusFromKey(requestId));
  } catch {
    // Blocked storage only costs Back its origin after the round trip.
  }
};

export const storedStatusFrom = (requestId: string): string | null => {
  try {
    return fromOf({ from: sessionStorage.getItem(statusFromKey(requestId)) });
  } catch {
    return null;
  }
};

const isSeedProject = (project: unknown) =>
  project === null ||
  (isRecord(project) &&
    isString(project._id) &&
    isString(project.name) &&
    isStringOrNull(project.number));

const isSeedSupplier = (supplier: unknown) =>
  isRecord(supplier) &&
  isString(supplier._id) &&
  isString(supplier.name) &&
  (supplier.areaId === undefined || isString(supplier.areaId));

// The seed crosses a history entry, which survives reloads and older bundles: check every field.
export const readWizardSeed = (state: unknown): WizardSeed | null => {
  const seed = isRecord(state) ? state.seed : undefined;
  if (!isRecord(seed)) return null;
  const valid =
    isSeedProject(seed.project) &&
    isListOf(seed.suppliers, isSeedSupplier) &&
    (seed.preselectWinners === undefined || typeof seed.preselectWinners === 'boolean');
  return valid ? (seed as WizardSeed) : null;
};
